import React, { useState, useRef, useMemo } from 'react';
import {
  Dropdown,
  Checkbox,
  Radio,
  Bubble,
  SearchBox,
} from 'tea-component';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import SvgIcon from '@/pages/home/components/SvgIcon';
import '@/pages/home/assets/scss/components/selectComponents.scss';
import { IDimensionFilterProps } from './type';
import './index.scss';

export const DIMENSION_ALL_VALUE = '__all__';

const DimensionFilter = (props: IDimensionFilterProps) => {
  const { value, options, onChange } = props;
  const { getLocalText } = useLocal('filter');
  const { getLocalText: getSelectorLocalText } = useLocal('selector_multiple_component');
  const [isHover, setIsHover] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const bindClassName = useRef(
    `dimension-filter-${Math.round(Math.random() * 10000 * Date.now()).toString(16)}`,
  );

  const isAll = value.length === 1 && value[0] === DIMENSION_ALL_VALUE;
  const isNoneSelected = value.length === 0;
  const dimensionValues = useMemo(() => value.filter(v => v !== DIMENSION_ALL_VALUE), [value]);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    const lower = searchValue.toLowerCase();
    return options.filter(opt => opt.text.toLowerCase().includes(lower));
  }, [options, searchValue]);

  const handleRadioChange = (radioVal: string) => {
    if (radioVal === DIMENSION_ALL_VALUE) {
      onChange([DIMENSION_ALL_VALUE]);
    }
  };

  const handleCheckChange = (nextValue: string[]) => {
    onChange(nextValue);
  };

  const isClear = filteredOptions.some(opt => dimensionValues.includes(opt.value));

  const handleToggleAll = () => {
    if (isClear) {
      const filteredValues = new Set(filteredOptions.map(opt => opt.value));
      const remaining = dimensionValues.filter(v => !filteredValues.has(v));
      onChange(remaining.length > 0 ? remaining : []);
    } else {
      const newValues = new Set([...dimensionValues, ...filteredOptions.map(opt => opt.value)]);
      onChange([...newValues]);
    }
  };

  const allText = getSelectorLocalText('common_all_describe');
  const pleaseSelectText = getLocalText('please_select');

  const buttonText = useMemo(() => {
    if (isNoneSelected) return pleaseSelectText;
    if (isAll) return allText;
    if (dimensionValues.length <= 2) {
      return dimensionValues
        .map(v => options.find(opt => opt.value === v)?.text || v)
        .join(', ');
    }
    return `${dimensionValues.length}`;
  }, [isNoneSelected, isAll, dimensionValues, options, pleaseSelectText, allText]);

  const buttonContent = useMemo(() => {
    if (isNoneSelected) return pleaseSelectText;
    if (isAll) return allText;
    return <div className="dbd-select-button-value-only-text">{buttonText}</div>;
  }, [isNoneSelected, isAll, pleaseSelectText, allText, buttonText]);

  const button = (
    <div className="dbd-dimension-filter__button select-button">
      <span className="dbd-select-button-value">
        {buttonContent}
      </span>
    </div>
  );

  const allMatchText = useMemo(() => {
    if (!searchValue) return allText;
    const lower = searchValue.toLowerCase();
    if (allText.toLowerCase().includes(lower)) {
      const regex = new RegExp(`(${searchValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return allText.replace(regex, '<span class="is-highlight">$1</span>');
    }
    return allText;
  }, [searchValue, allText]);

  const isMatchAll = searchValue ? allMatchText !== allText : true;
  const isMatch = isMatchAll || filteredOptions.length > 0;

  return (
    <Dropdown
      clickClose={false}
      className={`select-dashboard-box ${bindClassName.current} dbd-dimension-filter`}
      button={button}
      appearance="button"
      onOpen={() => setSearchValue('')}
      closeOnScroll={false}
      positionFixed={false}
      boxClassName="selector-multiple__dropdown"
      overlayClassName="selector-multiple__dropdown-overlay"
      popupContainer={() => document.querySelector(`.${bindClassName.current}`)}
    >
      <SearchBox
        placeholder={getSelectorLocalText('placeholder')}
        className="selector-multiple-search-input"
        onChange={setSearchValue}
        value={searchValue}
      />
      <div className="selector-multiple__dropdown-inner">
        <div className="select-context">
          {/* {isMatchAll && (
            <>
              <div className="select-header-title">
                <span className="label">{getSelectorLocalText('overall')}</span>
              </div>
              <div className="select-header-felx">
                <Radio.Group
                  onChange={handleRadioChange}
                  value={isAll ? DIMENSION_ALL_VALUE : ''}
                >
                  <Radio name={DIMENSION_ALL_VALUE}>
                    <span dangerouslySetInnerHTML={{ __html: allMatchText }} />
                    <div
                      className="select-header-icon-box"
                      onMouseOver={() => setIsHover(true)}
                      onMouseOut={() => setIsHover(false)}
                    >
                      {' '}
                      <Bubble
                        trigger="hover"
                        arrowPointAtCenter
                        placement="top-start"
                        content={getLocalText('dimension_all_info')}
                        dark
                      >
                        <i>
                          <SvgIcon
                            className="select-header-ic"
                            type={isHover ? 'alert-circle-action' : 'alert-circle-m'}
                          />
                        </i>
                      </Bubble>
                    </div>
                  </Radio>
                </Radio.Group>
              </div>
            </>
          )} */}
          {filteredOptions.length > 0 && (
            <div className="select-felx">
              <span className="select-felx-lf">
                {getLocalText('dimension_filter')}
              </span>
              <span onClick={handleToggleAll} className="select-felx-rt">
                {isClear
                  ? getSelectorLocalText('clear_all')
                  : getSelectorLocalText('select_all')}
              </span>
            </div>
          )}
        </div>
        {isMatch ? (
          <div className="list-container">
            <div className="tea-form-checks">
              <Checkbox.Group
                layout="column"
                value={dimensionValues}
                className="list"
                onChange={handleCheckChange}
              >
                {filteredOptions.map(item => (
                  <Checkbox key={item.value} name={item.value} disabled={item.disabled}>
                    <span>{item.text}</span>
                    {item.info && (
                      <Bubble
                        trigger="hover"
                        arrowPointAtCenter
                        placement="top-start"
                        content={item.info}
                        dark
                      >
                        <i className="dbd-dimension-filter__info-icon">
                          <SvgIcon className="select-header-ic" type="alert-circle-m" />
                        </i>
                      </Bubble>
                    )}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </div>
          </div>
        ) : (
          <div className="dbd-dimension-filter__no-data">{getSelectorLocalText('no_data')}</div>
        )}
      </div>
    </Dropdown>
  );
};

export default DimensionFilter;
